import React, { useMemo, useState } from 'react';

import DropDownSearchable from '../../dropDownSearchable';
import {
    formatDateMonthYYYY,
    formatDateMonthDDYYYY,
} from '../../../utilities/dateHelpers';
import {
    getTotalDaysByCity,
    getCutOffDatesByCity,
} from '../../../utilities/acquisitionsHelpers';
import TargetSummary from '../components/targetSummary';
import { colors } from '../../../constants';

const AcquisitionsMonthlyDashboardContents = ({ overview, assumptions }) => {
    const monthChoices = useMemo(() => {
        const allDates = Object.keys(overview);
        const unique = Array.from(new Set(allDates));
        return unique.map((timestamp) => {
            const date = new Date(timestamp);
            return {
                value: timestamp,
                label: formatDateMonthYYYY(date),
            };
        });
    }, [overview]);
    const [selectedMonth, setSelectedMonth] = useState(monthChoices[0]);

    const totalDaysByCity = useMemo(
        () => getTotalDaysByCity(assumptions),
        [assumptions]
    );

    const cutOffDatesByCity = useMemo(
        () =>
            getCutOffDatesByCity(
                totalDaysByCity,
                overview[selectedMonth.value].targets
            ),
        [(totalDaysByCity, selectedMonth)]
    );

    const handleMonthChange = (selected) => {
        setSelectedMonth(selected);
    };

    const selectedTargets = overview[selectedMonth.value].targets;

    const nSelectedGoal = overview[selectedMonth.value]?.monthSecuredGoal || 0;
    const nSelectedActual =
    overview[selectedMonth.value]?.monthSecuredActual || 0;

    const getStatusDescription = () => {
        if (nSelectedActual === nSelectedGoal) {
            return 'Completed Successfully';
        }
        const anyGreen = Object.keys(cutOffDatesByCity).some((city) =>
            Object.values(cutOffDatesByCity[city]).some(
                (co) => co.status === colors.aroGreen
            )
        );
        const allRed = Object.keys(cutOffDatesByCity).every((city) =>
            Object.values(cutOffDatesByCity[city]).every(
                (co) => co.status === colors.aroRed
            )
        );
        return anyGreen ? 'On Track' : allRed ? 'Off Track' : 'At Risk';
    };

    return (
        <div className="acquisitions-dashboard-container">
            <div className="mb-4" style={{ width: '200px' }}>
                <DropDownSearchable
                    options={monthChoices}
                    value={selectedMonth}
                    onSelect={handleMonthChange}
                    sortOptions={false}
                    compact={true}
                />
            </div>
            <div className="p-4 rounded-3 bg-white" style={{ width: 'fit-content' }}>
                <h2 className="fs-3 fw-normal">{selectedMonth.label} Sales</h2>
                <div className="table-responsive-sm">
                    <table
                        className="table table-bordered table-md align-middle table-hover"
                        style={{ tableLayout: 'fixed', width: '100%' }}
                    >
                        <colgroup>
                            <col className="acquisitions-dashboard-col" />
                        </colgroup>
                        <thead>
                            <tr>
                                <th scope="col" className="fs-4 text-center">
                  Targets
                                </th>
                                {assumptions.map((a) => (
                                    <th key={a.label} scope="col">
                                        <div className="fs-6 text-center">{a.label}</div>
                                        <div className="acquisitions-dashboard-header-legend">
                                            {totalDaysByCity[a.label] || '??'} Days
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {Object.keys(selectedTargets).map((tgt, i) => (
                                <tr key={tgt}>
                                    <td>
                                        <TargetSummary
                                            index={i}
                                            tgtTimeStamp={tgt}
                                            targetData={selectedTargets[tgt]}
                                        />
                                    </td>
                                    {Object.keys(cutOffDatesByCity).map((c) => (
                                        <td key={`${tgt}-${c}`}>
                                            <div
                                                className={`text-center acquisitions-dashboard-table-label ${cutOffDatesByCity[c][tgt].status}`}
                                            >
                                                {formatDateMonthDDYYYY(cutOffDatesByCity[c][tgt].date)}
                                            </div>
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="fs-4 py-1 px-1">
                    {nSelectedActual}/{nSelectedGoal} Secured - {getStatusDescription()}
                </div>
            </div>
        </div>
    );
};

const AcquisitionsMonthlyDashboard = ({ overview, assumptions }) => {
    return assumptions.length === 0 ? (
        <div className="acquisitions-dashboard-container">
            <p>No Assumptions. Define assumptions in the assumptions tab.</p>
        </div>
    ) : (
        <AcquisitionsMonthlyDashboardContents
            overview={overview}
            assumptions={assumptions}
        />
    );
};

export default AcquisitionsMonthlyDashboard;
